// src/contexts/auth/helpers.ts
/** @jsxImportSource @emotion/react */

import type { InvitationMagicLinkPayload, RegistrationData } from "./types";
import {
    ACCESS_TOKEN_KEY,
    clearStoredSession,
    EXPIRES_AT_KEY,
    readStoredSession,
    REFRESH_TOKEN_KEY,
    USER_ID_KEY,
    writeStoredSession,
} from "../../api/token-storage";
import { clearSession } from "../../api/api";
import { storageSet, storageRemove } from "../../platform/storage";
import { makeAuthLogger } from "./hooks/storage";

/* -------------------------------- Storage --------------------------------- */

export { ACCESS_TOKEN_KEY, EXPIRES_AT_KEY, REFRESH_TOKEN_KEY, USER_ID_KEY };

export const readStoredTokens = () => {
    const s = readStoredSession();
    return {
        access_token: s.access_token,
        refresh_token: s.refresh_token,
        expires_at: s.expires_at,
    };
};

export const writeStoredTokens = (tokens: {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    expires_at?: number;
}) => {
    writeStoredSession(tokens);
};

export const clearStoredTokens = () => {
    // keep legacy behavior: caller emits if needed
    clearStoredSession();
    clearSession({ emit: false });
};

export const persistStoredUserId = (u: any | null | undefined) => {
    const id = String(u?.id ?? "").trim();
    if (!id) return;
    storageSet(USER_ID_KEY, id);
};

export const clearStoredUserId = () => {
    storageRemove(USER_ID_KEY);
};

export const readStoredUserId = (): string => {
    try {
        return localStorage.getItem(USER_ID_KEY) ?? "";
    } catch {
        return "";
    }
};

/* -------------------- Invitation payload -> onboarding patch -------------------- */

export const getHashOrQueryParams = () => {
    const out: Record<string, string> = {};

    const rawHash = window.location.hash?.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash || "";
    if (rawHash) {
        const hp = new URLSearchParams(rawHash);
        hp.forEach((v, k) => {
            out[k] = v;
        });
    }

    const qp = new URLSearchParams(window.location.search);
    qp.forEach((v, k) => {
        if (!(k in out)) out[k] = v;
    });

    return out;
};

export const scrubMagicLinkFromUrl = () => {
    if (window.location.hash) {
        window.history?.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.search,
        );
    }
};

export const safeParseJson = <T>(raw: string | null): T | null => {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
};

export const coerceOnboardingFromInvitation = (
    payload: InvitationMagicLinkPayload,
): {
    userId?: string;
    data: Partial<RegistrationData>;
} => {
    const L = makeAuthLogger("helpers");

    L.log("[coerceInvitation] payload keys", {
        hasProfile: !!payload?.profile,
        email: payload?.email ? "[present]" : "[absent]",
        action_link: payload?.action_link ? "[present]" : "[absent]",
        redirectTo: payload?.redirectTo ? "[present]" : "[absent]",
        profileKeys: payload?.profile ? Object.keys(payload.profile) : [],
    });

    const p = payload?.profile ?? {};

    const resolvedEmail = payload?.email ?? p.email_address ?? undefined;

    const resolvedFirstName = p.first_name ?? undefined;
    const resolvedLastName = p.last_name ?? undefined;
    const resolvedNickname = p.nickname ?? undefined;

    const resolvedFullName = resolvedFirstName || resolvedLastName
        ? `${resolvedFirstName ?? ""}${
            resolvedFirstName && resolvedLastName ? " " : ""
        }${resolvedLastName ?? ""}`.trim()
        : undefined;

    const resolvedCompany = p.company_name ?? undefined;
    const resolvedTimezone = p.preferred_timezone ?? undefined;

    const resolvedNotificationMethod = p.notification_method ?? undefined;
    const resolvedPhoneNumber = p.mobile_number ?? undefined;

    const resolvedBiometricsEnabled = p.biometrics_enabled ?? undefined;
    const resolvedBiometricType = p.biometrics_type ?? undefined;

    const resolvedRegistrationCompleted = p.registration_completed ?? undefined;
    const resolvedOnboardingStep = typeof p.registration_step === "number"
        ? p.registration_step
        : undefined;

    const resolvedOnboardingCompleted = p.onboarding_completed ?? undefined;

    const resolvedSmsConsent = p.sms_consent ?? undefined;

    const data: Partial<RegistrationData> = {};

    if (resolvedEmail !== undefined) {
        data.email = resolvedEmail;
        data.authProvider = "email";
    }

    if (resolvedFirstName !== undefined) data.firstName = resolvedFirstName;
    if (resolvedLastName !== undefined) data.lastName = resolvedLastName;
    if (resolvedNickname !== undefined) data.nickname = resolvedNickname;
    if (resolvedFullName !== undefined) data.fullName = resolvedFullName;

    if (resolvedCompany !== undefined) data.company = resolvedCompany;
    if (resolvedTimezone !== undefined) data.timezone = resolvedTimezone;

    if (resolvedNotificationMethod !== undefined) {
        data.notificationMethod = resolvedNotificationMethod as
            | "sms"
            | "email"
            | "both";
    }
    if (resolvedPhoneNumber !== undefined) {
        data.phoneNumber = resolvedPhoneNumber;
    }

    if (resolvedBiometricsEnabled !== undefined) {
        data.biometricsEnabled = resolvedBiometricsEnabled;
    }
    if (resolvedBiometricType !== undefined) {
        data.biometricType = resolvedBiometricType as
            | "face"
            | "fingerprint"
            | "unknown";
    }

    if (resolvedRegistrationCompleted !== undefined) {
        data.registration_completed = resolvedRegistrationCompleted;
    }
    if (resolvedOnboardingStep !== undefined) {
        data.registration_step = resolvedOnboardingStep;
    }
    if (resolvedOnboardingCompleted !== undefined) {
        data.onboarding_completed = resolvedOnboardingCompleted;
    }

    if (resolvedSmsConsent !== undefined) data.smsConsent = resolvedSmsConsent;

    const userId = p.id ?? undefined;

    if (userId !== undefined) return { userId, data };
    return { data };
};
